
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { FriendRequest } from '../types';

export const useFriendRequests = (userId: string | undefined) => {
  const [friendRequests, setFriendRequests] = useState<Record<string, FriendRequest>>({});
  const [loading, setLoading] = useState(false);
  
  // Fetch all friend requests for the current user
  useEffect(() => {
    const fetchFriendRequests = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        // Get requests sent by the current user
        const { data: sentRequests, error: sentError } = await supabase
          .from('friend_requests')
          .select('*')
          .eq('requester_id', userId);
          
        if (sentError) throw sentError;
        
        // Get requests received by the current user
        const { data: receivedRequests, error: receivedError } = await supabase
          .from('friend_requests')
          .select('*')
          .eq('recipient_id', userId);
          
        if (receivedError) throw receivedError;
        
        // Combine and map by recipient_id for sent requests
        // and requester_id for received requests
        const requestMap: Record<string, FriendRequest> = {};
        
        sentRequests?.forEach(request => {
          requestMap[request.recipient_id] = request as FriendRequest;
        });
        
        receivedRequests?.forEach(request => {
          requestMap[request.requester_id] = request as FriendRequest;
        });
        
        setFriendRequests(requestMap);
      } catch (error) {
        console.error('Error fetching friend requests:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFriendRequests();
  }, [userId]);
  
  const sendFriendRequest = async (recipientId: string) => {
    if (!userId) {
      toast.error('You must be logged in to send friend requests');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('friend_requests')
        .insert({
          requester_id: userId,
          recipient_id: recipientId,
          status: 'pending'
        })
        .select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Update local state
        setFriendRequests(prev => ({
          ...prev,
          [recipientId]: data[0] as FriendRequest
        }));
        
        toast.success('Friend request sent');
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
      toast.error('Failed to send friend request');
    }
  };
  
  const acceptFriendRequest = async (requesterId: string) => {
    if (!userId || !friendRequests[requesterId]) return;
    
    try {
      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'accepted' })
        .eq('id', friendRequests[requesterId].id);
      
      if (error) throw error;
      
      // Update local state
      setFriendRequests(prev => ({
        ...prev,
        [requesterId]: {
          ...prev[requesterId],
          status: 'accepted'
        }
      }));
      
      toast.success('Friend request accepted');
    } catch (error) {
      console.error('Error accepting friend request:', error);
      toast.error('Failed to accept request');
    }
  };
  
  return {
    friendRequests,
    loading: loading,
    sendFriendRequest,
    acceptFriendRequest
  };
};
